"use client";
import { useTranslations } from "next-intl";
import { IBaseLabelValue } from "@/interfaces";
import { useRouter } from "next/navigation";
import {
  Accordion,
  CustomButton,
  FlexBetween,
  FlexCenter,
  FlexCol,
} from "../../ui";
import InputDataContent from "../inputDataContent";
import { cn, formatNumberWithDots } from "@/lib";
import { routes } from "@/routes";
import DownloadPdf from "@/components/pdfTemplates/downloadPdf";
import { useRef, useState } from "react";
import PdfTemplate from "@/components/pdfTemplates/pdfTemplate";
import ResultContent from "@/components/resultContent";
import { IRawPurchasedCompanyNewspaperInquiryResData } from "@/interfaces/inquiries/legal";

const NEWSPAPER_MAP = {
  newsID: "شناسه آگهی",
  title: "عنوان",
  nationalID: "شناسه ملی شرکت",
  city: "نوع روزنامه",
  publicationDate: "تاریخ روزنامه",
  number: "شماره روزنامه",
  page: "شماره صفحه",
  letterDate: "تاریخ نامه",
  letterNumber: "شماره نامه (مکانیزه)",
  capital: "سرمایه اولیه",
  description: "جزئیات",
};

const CompanyNewspaperInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedCompanyNewspaperInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const response = data.data.data.data;
  const { national_code } = data.data.input;
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("companyInformationInquiry.nationalId"),
      value: national_code,
    },
  ];
  const [openAccordionId, setOpenAccordionId] = useState<number | null>(1);
  const pdfContentRef = useRef<HTMLDivElement>(null);

  const companyNewspapers = response.map((item, index: number) => {
    return {
      title: item.publicationDate,
      key: index + 1,
      data: Object.keys(item).map((key) => ({
        label: NEWSPAPER_MAP[key as keyof typeof NEWSPAPER_MAP],
        value:
          key === "capital"
            ? item[key]
              ? `${formatNumberWithDots(item[key])} ریال `
              : "-"
            : (item[key as keyof typeof item] ?? "-"),
      })),
    };
  });

  return (
    <div className="shadow-main from-sunset-gold relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="w-full items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-8 text-center font-bold lg:text-lg">
          استعلام روزنامه رسمی شرکت
        </h2>
        <InputDataContent content={inputContent} />
        <FlexBetween className="mb-4 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("companyInformationInquiry.resultTitle")}
          </h2>
          <DownloadPdf
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            id="company-members-inquiry-download-pdf"
            resultContent={[]}
            fileName={` گزارش اطلاعات اعضا شرکت ${national_code}`}
            tableTitle={` گزارش اطلاعات اعضا شرکت ${national_code}`}
          />
        </FlexBetween>
        {companyNewspapers && (
          <FlexCol>
            {companyNewspapers.map((item, i: number) => (
              <div key={i} className="mb-3">
                <Accordion
                  title={item.title}
                  open={item.key === openAccordionId}
                  hasIcon={false}
                  onOpenChange={(isOpen) => {
                    setOpenAccordionId(isOpen ? item.key : null);
                  }}
                >
                  <ResultContent content={item.data} />
                </Accordion>
              </div>
            ))}
          </FlexCol>
        )}
        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            id="company-newspaper-inquiry-download-pdf"
            resultContent={[]}
            fileName={` گزارش آگهی های روزنامه رسمی شرکت ${national_code}`}
            tableTitle={` گزارش آگهی های روزنامه رسمی شرکت $${national_code}`}
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-company-newspaper-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-company-newspaper-inquiry-new-request"
              onClick={() =>
                router.push(
                  routes.services.Company_Newspaper_inquiry.rewritePath,
                )
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
        {/* This div will be used for PDF generation - DetailCreditCard style */}
        <div
          ref={pdfContentRef}
          style={{
            position: "fixed",
            left: "-9999px",
            top: "-9999px",
            width: "800px",
            fontFamily: "IRANYekan, sans-serif",
            margin: "2rem auto",
          }}
        >
          {companyNewspapers && (
            <>
              {companyNewspapers.map((item, index) => (
                <PdfTemplate
                  key={index}
                  tableTitle={` گزارش آگهی های روزنامه رسمی شرکت ${national_code}`}
                  resultContent={item.data}
                  code={data.code}
                  dateTime={data.created_at}
                />
              ))}
            </>
          )}
        </div>
      </FlexCol>
    </div>
  );
};

export default CompanyNewspaperInquiryDetails;
